import {mapObject} from '@benchling/js-utils';
import produce from 'immer';
import {uniq} from 'underscore';

import {assertAndContinue} from 'Asserts';
import BenchlingConfig from 'BenchlingConfig';
import {ProcedureParameterConfirmationMethod} from 'graphql-client/__generated/graphqlGlobalTypes';
import {NotebookColumnItemType} from 'lookup-config/LookupConfigModalConstants';
import addParametersFromMethod from 'notebook/integrations/procedures/add-parameters-to-table/addParametersFromMethod';
import {
  ColumnInfo,
  getParameterStructuredTableColumnInfos,
  ParameterStructuredTableColumnUuid,
} from 'notebook/integrations/procedures/ParameterStructuredTableColumnInfo';
import {
  ParameterInfo,
  ParameterStructuredTableMode,
  ParameterStructuredTableModeToIcon,
  ProcedureParameterSchemaFieldName,
} from 'notebook/integrations/procedures/ParameterStructuredTableConstants';
import {
  getTableSchemaMetadatasFromParameterInfos,
  getTableSchemasFromParameterInfos,
} from 'notebook/integrations/procedures/ProceduresUtil';
import {addToWorklistActionFunction} from 'notebook/structured-tables/action-functions/addToWorklistActionFunction';
import {downloadCSVActionFunction} from 'notebook/structured-tables/action-functions/downloadCSVActionFunction';
import {exportAuditLogsActionFunction} from 'notebook/structured-tables/action-functions/exportAuditLogsActionFunction';
import {refreshTableActionFunction} from 'notebook/structured-tables/action-functions/refreshTableActionFunction';
import {submitParameterActionFunction} from 'notebook/structured-tables/action-functions/submitParametersActionFunction';
import submitParametersRowsActionFunction from 'notebook/structured-tables/action-functions/submitParametersRowsActionFunction';
import {validateStructuredTableActionFunction} from 'notebook/structured-tables/action-functions/validateStructuredTableActionFunction';
import {ColumnDefinition, getColumnLabel} from 'notebook/structured-tables/columns/ColumnDefinitions';
import GenericStructuredTableAdapter from 'notebook/structured-tables/GenericStructuredTableAdapter';
import type {NotebookColumnInfo} from 'notebook/structured-tables/lookup-columns/runLookupConfigModalForNotebook';
import {ParameterStructuredTableRowUpdater} from 'notebook/structured-tables/row-updaters/ParameterStructuredTableRowUpdater';
import {
  ActionFunctions,
  CreateStructuredTableProps,
  EvalRowView,
  EvalTableView,
  GetCustomEvalArgsOptions,
  PrimarySample,
  PrimarySampleLookupType,
  SchemaAndColumnGroup,
  StructuredTable,
  StructuredTableAddFromSourceType,
  StructuredTableAddOperation,
  StructuredTableAddOperationParameters,
  StructuredTableColumn,
  StructuredTableColumnGroups,
  StructuredTableKind,
  StructuredTableRow,
  StructuredTableSchema,
} from 'notebook/structured-tables/StructuredTableConstants';
import {shouldValidateStructuredTableSchema} from 'notebook/structured-tables/util/shouldValidateStructuredTable';
import {
  makeSchemaFieldStructuredColumn,
  makeStructuredTableColumn,
} from 'notebook/structured-tables/util/StructuredTableColumnUtil';
import {StructuredTableInsertionContextValue} from 'notebook/structured-tables/util/StructuredTableInsertionContext';
import {EvaluatedCell} from 'notebook/tables/excess/Evaluator';
import {CustomResolutionQueryType} from 'notebook/tables/string-resolution/StringResolutionConstants';
import {
  CellIndex,
  ColumnConstraint,
  CustomEvalFnAndType,
  CustomEvalResult,
  CustomEvalType,
  RowMetadata,
  Table,
  TableColumnType,
  TableData,
  TypedValue,
} from 'notebook/tables/TableConstants';
import {ResolutionQueryType} from 'notebook/tables/TableStoreTransforms';
import TableUtil from 'notebook/tables/TableUtil';
import {
  makeTypedValueFromCustomResolvedValue,
  ResolveQueryFn,
} from 'notebook/tables/typed-values/TypedValueResolution';
import {FieldType} from 'schemas/fields/SchemaFieldConstants';
import {SchemaFieldSelector} from 'schemas/SchemaFieldSelectorConstants';
import {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';
import {Optional} from 'util/Types';

// Schema fields that always resolve to entity-typed values, safe to use as lookup sources.
// Includes both planned and confirmed columns (e.g. "Planned Material" and "Material Used").
// Excludes AMOUNT (numeric), VALUE (heterogeneous), and shouldDelegateColumnByDataType columns.
const ENTITY_TYPED_SCHEMA_FIELDS = [
  ProcedureParameterSchemaFieldName.MATERIAL_FIELD_NAME,
  ProcedureParameterSchemaFieldName.EQUIPMENT_FIELD_NAME,
];

export interface ParameterStructuredTableRowMetadata extends RowMetadata {
  entityId?: string;
  // since each row has a parameter and schema version, this makes it a lot easier to keep track of which
  // parameter each row is associated with.
  parameterId: string;
  parameterVersionId: string;
  isPlateParameter: boolean;
  // Indicates whether a parameter has been tombstoned from parameter definition. When true, the
  // parameter row on step document will be grayed out and asking for row deletion.
  isTombstoned?: boolean;
  plannedValueId?: string;
  /** For parallel execution, this is the PMEI id that signifies which Condition-Replicate the Parameter
   * is from. */
  methodExecutionInstanceId?: string;
  /** The FieldType of the parameter (e.g. FieldType.DATETIME, FieldType.FLOAT).
   * Used by the Min-Max column renderer to determine formatting. */
  parameterDataType?: FieldType;
}

export interface ParameterStructuredTableMetadata {
  mode: ParameterStructuredTableMode;
  isMultibatch?: boolean;
}

export type ParameterStructuredTable = StructuredTable<
  ParameterStructuredTableMetadata,
  ParameterStructuredTableRowMetadata
>;

export const PARAMETER_TABLE_MODE_TO_DISPLAY_NAME: Record<ParameterStructuredTableMode, string> = {
  [ParameterStructuredTableMode.PARAMETER_CONFIRMATION]: 'Parameters',
  [ParameterStructuredTableMode.PARAMETER_ACTUAL]: 'Record parameter measurements',
  [ParameterStructuredTableMode.MATERIAL_INPUT_CONFIRMATION]: 'Material inputs',
  [ParameterStructuredTableMode.MATERIAL_OUTPUT_CONFIRMATION]: 'Material outputs',
  [ParameterStructuredTableMode.EQUIPMENT_CONFIRMATION]: 'Equipment',
};

// Overrides for the "Add from source" menu item
export const PARAMETER_TABLE_MODE_TO_MENU_ITEM_NAME: Record<ParameterStructuredTableMode, string> = mapObject(
  {
    ...PARAMETER_TABLE_MODE_TO_DISPLAY_NAME,
    [ParameterStructuredTableMode.PARAMETER_ACTUAL]: 'parameter measurements',
  },
  (s) => `Add ${s.toLowerCase()}`
);

export const EMPTY_DELEGATED_COLUMN = makeStructuredTableColumn({
  columnType: TableColumnType.STRING,
  name: '_empty',
});

/** Just a plain string column to hold information about plates */
export const PLATE_STRING_DELEGATED_COLUMN = makeStructuredTableColumn({
  columnType: TableColumnType.STRING,
  name: '_plate',
});

export const CONFIRM_IN_PLATE_MAP_TEXT = 'Confirm in plate map';

const CONFIRM_EQUIPMENT_COLUMN_UUID = ParameterStructuredTableColumnUuid.PRIMARY_VALUE_COLUMN_UUID;

function confirmEquipmentCustomEval(
  equipmentTypedValue: TypedValue,
  columnDefinition: Optional<ColumnDefinition>,
  cellDependencies: Array<TypedValue | null>,
  {
    tableMetadata,
  }: {
    tableMetadata: ParameterStructuredTableMetadata;
    rowMetadata: ParameterStructuredTableRowMetadata;
  },
  resolveFromResolutionQuery: ResolveQueryFn
): CustomEvalResult {
  const defaultEvalResult = {val: equipmentTypedValue, error: null, locked: false};

  if (
    !equipmentTypedValue ||
    equipmentTypedValue.type !== FieldType.BIOENTITY_LINK ||
    equipmentTypedValue.targetFiles.length === 0
  ) {
    return defaultEvalResult;
  }

  const modelTypedValue = cellDependencies[0];
  if (!modelTypedValue || modelTypedValue.type !== FieldType.BIOENTITY_LINK) {
    return defaultEvalResult;
  }

  const {resolvedValue, error} = resolveFromResolutionQuery(
    equipmentTypedValue,
    {
      type: ResolutionQueryType.CUSTOM,
      customResolutionQueryType: CustomResolutionQueryType.PROCEDURES_VALIDATE_EQUIPMENT_CONFIRMATION,
      bioentityId: equipmentTypedValue.targetFiles[0].id ?? '',
      equipmentTypedValue,
      modelTypedValue,
    },
    makeTypedValueFromCustomResolvedValue
  );
  return {val: resolvedValue, error, locked: false};
}

export default class ParameterStructuredTableAdapter extends GenericStructuredTableAdapter<
  ParameterStructuredTableMetadata,
  ParameterStructuredTableRowMetadata
> {
  override getTableSpecificCustomEvalFn(
    tableView: EvalTableView<ParameterStructuredTableMetadata, ParameterStructuredTableRowMetadata>,
    cellIndex: CellIndex
  ): CustomEvalFnAndType<{
    tableMetadata: ParameterStructuredTableMetadata;
    rowMetadata: ParameterStructuredTableRowMetadata;
  }> | null {
    const {column} = this.getRowAndColumnForCellIndex(tableView, cellIndex);
    const {metadata} = tableView;
    if (
      metadata.mode === ParameterStructuredTableMode.EQUIPMENT_CONFIRMATION &&
      column.provenanceUuid === CONFIRM_EQUIPMENT_COLUMN_UUID
    ) {
      return {
        customEvalFn: confirmEquipmentCustomEval,
        customEvalType: CustomEvalType.DEFAULT,
      };
    }
    return null;
  }

  override getTableSpecificCustomEvalArgs(
    tableView: EvalTableView<ParameterStructuredTableMetadata, ParameterStructuredTableRowMetadata>,
    cellIndex: CellIndex,
    options: GetCustomEvalArgsOptions
  ): Array<string> {
    const {toSpreadsheetIndex} = options;
    const {columns, metadata} = tableView;
    const {row: currentRowIndex, column: currentColumnIndex} = cellIndex;
    if (
      metadata.mode === ParameterStructuredTableMode.EQUIPMENT_CONFIRMATION &&
      columns[currentColumnIndex].provenanceUuid === CONFIRM_EQUIPMENT_COLUMN_UUID // Confirm Equipment column
    ) {
      const modelColumnIndex = columns.findIndex(
        (column) => column.provenanceUuid === ParameterStructuredTableColumnUuid.EQUIPMENT_MODEL_COLUMN_UUID
      );
      if (modelColumnIndex === -1) {
        return [];
      }
      return [`=${toSpreadsheetIndex({row: currentRowIndex, column: modelColumnIndex})}`];
    }
    return [];
  }

  getBlankRowMetadata(tableMetadata: ParameterStructuredTableMetadata): ParameterStructuredTableRowMetadata {
    return {
      delegatedColumns: {},
      // These are empty strings here but MUST be replaced with actual values when rows are added to the table
      parameterId: '',
      parameterVersionId: '',
      isPlateParameter: false,
      isTombstoned: false,
    };
  }
  getDisplayName(structuredTable: ParameterStructuredTable): string {
    return PARAMETER_TABLE_MODE_TO_DISPLAY_NAME[structuredTable.metadata.mode];
  }

  override allowSingleInputs(_: ParameterStructuredTable): boolean {
    return true;
  }

  override allowMergedCellRangeInColumn(
    structuredTable: ParameterStructuredTable,
    column: StructuredTableColumn
  ): boolean {
    // Allow merging only on Comments, Added At, Container Used, and Recorded At columns
    return (
      column.provenanceUuid === ParameterStructuredTableColumnUuid.COMMENTS_COLUMN_UUID ||
      column.provenanceUuid === ParameterStructuredTableColumnUuid.CONFIRMED_AT_COLUMN_UUID ||
      column.provenanceUuid === ParameterStructuredTableColumnUuid.MATERIAL_CONTAINER_COLUMN_UUID
    );
  }

  override getPrimarySamples(
    structuredTable: ParameterStructuredTable,
    computedTable?: Array<Array<EvaluatedCell>> | null
  ): {[rowId: string]: Array<PrimarySample>} {
    const primarySamplesByRowId = {} as {
      [rowId: string]: Array<PrimarySample>;
    };
    for (const row of structuredTable.rows) {
      const samples: Array<PrimarySample> = [];

      if (row.metadata.parameterVersionId) {
        samples.push({
          schemaId: null,
          columnGroup: StructuredTableColumnGroups.REFERENCE,
          sampleLookupInfo: {
            sampleType: PrimarySampleLookupType.DIRECT,
            idForLookup: row.metadata.parameterVersionId,
          },
        });
      }

      primarySamplesByRowId[row.rowId] = samples;
    }
    return primarySamplesByRowId;
  }

  override skipUnsubmittedIndicatorsForRow(
    structuredTable: ParameterStructuredTable,
    row: StructuredTableRow<ParameterStructuredTableRowMetadata>
  ): boolean {
    // always treat plate parameter rows as submitted
    return row.metadata.isPlateParameter;
  }

  override getTableKindRowUpdater(): typeof ParameterStructuredTableRowUpdater {
    return ParameterStructuredTableRowUpdater;
  }

  override getInitialColumns({
    entryId,
    metadata,
    schemas,
    selectorsById,
    structuredTableInsertionContext,
  }: {
    entryId: string | null;
    metadata: ParameterStructuredTableMetadata;
    schemas: Array<SchemaAndColumnGroup>;
    selectorsById: {[selectorId: string]: SchemaFieldSelector};
    structuredTableInsertionContext: StructuredTableInsertionContextValue;
  }): Array<StructuredTableColumn> {
    const expectedColumnInfos = getParameterStructuredTableColumnInfos(metadata.mode, {
      isMultibatch: metadata.isMultibatch,
    });
    return expectedColumnInfos.map(
      (col: ColumnInfo<ParameterInfo>): StructuredTableColumn => ({
        ...makeStructuredTableColumn({
          columnType: col.colType,
          name: col.colName,
          columnGroup: col.colGroup ?? null,
          isReadOnly: col.colGroup === StructuredTableColumnGroups.REFERENCE,
        }),
        provenanceUuid: col.colUuid,
        ...col.columnOverrides,
      })
    );
  }

  override getActionFunctions(
    tableMetadata: ParameterStructuredTableMetadata,
    schemas: Array<StructuredTableSchema>
  ): ActionFunctions<{}, ParameterStructuredTableRowMetadata> {
    const secondaryActions = [
      ...(BenchlingConfig.PROCEDURES_ENABLE_PARAMETER_LIBRARY ? [refreshTableActionFunction] : []),
      exportAuditLogsActionFunction,
      downloadCSVActionFunction,
    ];
    if (shouldValidateStructuredTableSchema(StructuredTableKind.PARAMETER, schemas)) {
      secondaryActions.push(validateStructuredTableActionFunction);
    }
    switch (tableMetadata.mode) {
      case ParameterStructuredTableMode.EQUIPMENT_CONFIRMATION:
      case ParameterStructuredTableMode.MATERIAL_INPUT_CONFIRMATION:
      case ParameterStructuredTableMode.MATERIAL_OUTPUT_CONFIRMATION:
        secondaryActions.unshift(addToWorklistActionFunction);
        break;
      default:
    }
    return {
      primary: [submitParameterActionFunction],
      secondary: secondaryActions,
      overflow: [],
      contextMenu: [submitParametersRowsActionFunction],
    };
  }

  override getAddFromSourceOperations(
    entryId: string,
    structuredTable?: ParameterStructuredTable
  ): Array<
    StructuredTableAddOperation<ParameterStructuredTableMetadata, ParameterStructuredTableRowMetadata>
  > {
    const onSelect: StructuredTableAddOperation<
      ParameterStructuredTableMetadata,
      ParameterStructuredTableRowMetadata
    >['onSelect'] = ({structuredTable, addFullRows, contexts}) => {
      addParametersFromMethod({
        destinationTable: structuredTable,
        addRowsFn: addFullRows,
        parameterSettings: contexts.procedureStepConfigEditorContext?.parameterSettings,
        parameterUsages: contexts.procedureStepConfigEditorContext?.parameterUsages,
      });
    };
    if (!structuredTable) {
      return [];
    }
    if (this.isMaterialOutputExecutionTable(structuredTable)) {
      return [];
    }
    const options = structuredTable?.metadata.mode
      ? {
          options: {
            menuItemNameOverride: PARAMETER_TABLE_MODE_TO_MENU_ITEM_NAME[structuredTable.metadata.mode],
          },
        }
      : {};
    return [
      {
        type: StructuredTableAddFromSourceType.ADD_FROM_PARAMETER_LIST,
        onSelect,
        ...options,
      },
    ];
  }

  override createTable(
    createProps: CreateStructuredTableProps<ParameterStructuredTableMetadata>
  ): Omit<ParameterStructuredTable, 'cid'> {
    let structuredTable = super.createTable(createProps);
    structuredTable = this.clearColumnDefaultValues(structuredTable);
    return structuredTable;
  }

  // Keep in sync with ParameterTableInstantiator.is_material_output_execution_table in
  // benchling/notebook/structured_tables/instantiators/parameter_table_instantiator.py
  /** Material Output tables in Execution are often a special case. This is just a utility function. */
  isMaterialOutputExecutionTable(structuredTable: ParameterStructuredTable): boolean {
    return (
      structuredTable.metadata.mode === ParameterStructuredTableMode.MATERIAL_OUTPUT_CONFIRMATION &&
      !structuredTable.isConfig
    );
  }

  override toTable(structuredTable: ParameterStructuredTable): Table {
    const table = super.toTable(structuredTable);
    return {
      ...table,
      lockedDims: true,
      allowAddRows: false,
      allowRowRemoval: this.allowDeleteTable(structuredTable).canDelete,
      allowColumnEdits: false,
      // Allow only lookup column insertion/deletion (not generic column editing) when the feature is enabled,
      // bypassing lockedDims specifically for lookup column operations.
      // Uses allowLookupColumnEdits instead of allowColumnEdits to avoid enabling the generic
      // schema-field column insert UI (Material, Amount, Container, etc.).
      allowLookupColumnEdits: this.enableLookupColumns(structuredTable),
    };
  }

  override enableLookupColumns(structuredTable: ParameterStructuredTable): boolean {
    if (!BenchlingConfig.ELN_PARAM_TABLE_LOOKUP_COLUMNS) {
      return false;
    }
    const mode = structuredTable.metadata.mode;
    return (
      mode === ParameterStructuredTableMode.MATERIAL_INPUT_CONFIRMATION ||
      mode === ParameterStructuredTableMode.MATERIAL_OUTPUT_CONFIRMATION ||
      mode === ParameterStructuredTableMode.EQUIPMENT_CONFIRMATION
    );
  }

  override getLookupInputColumns(
    structuredTable: ParameterStructuredTable,
    columnConstraints: Array<ColumnConstraint>
  ): Array<NotebookColumnInfo> {
    if (!this.enableLookupColumns(structuredTable)) {
      return [];
    }

    // Derive entity ROW_DELEGATE columns from the canonical column definitions
    // rather than hardcoding UUIDs, so this stays in sync automatically.
    const columnInfos = getParameterStructuredTableColumnInfos(structuredTable.metadata.mode, {
      isMultibatch: structuredTable.metadata.isMultibatch,
    });
    const entityColumnUuids = new Set(
      columnInfos
        .filter(
          (col) =>
            col.colType === TableColumnType.ROW_DELEGATE &&
            col.schemaFieldName != null &&
            ENTITY_TYPED_SCHEMA_FIELDS.includes(col.schemaFieldName)
        )
        .map((col) => col.colUuid)
    );

    // Exclude entity columns that are delegated to the plate-string delegate in any plate
    // parameter row. For those rows the lookup evaluator would receive a plain STRING value
    // and silently return empty cells, which is confusing.
    const plateStringDelegateName = PLATE_STRING_DELEGATED_COLUMN.name;
    const plateStringDelegateUuids = new Set(
      structuredTable.delegatedColumns
        .filter((c) => c.name === plateStringDelegateName)
        .map((c) => c.provenanceUuid)
    );
    const columnUuidsUsedByPlateRows = new Set(
      structuredTable.rows
        .filter((r) => r.metadata.isPlateParameter)
        .flatMap((r) =>
          Object.entries(r.metadata.delegatedColumns ?? {})
            .filter(([, delegateUuid]) => plateStringDelegateUuids.has(delegateUuid))
            .map(([colUuid]) => colUuid)
        )
    );

    // Start with the base extracted columns (entity, container, location, storable, etc.)
    // so that non-ROW_DELEGATE column types are not silently dropped.
    const baseColumns = super.getLookupInputColumns(structuredTable, columnConstraints);
    const seenUuids = new Set(baseColumns.map((c) => c.provenanceUuid));

    // Add ROW_DELEGATE entity columns that extractUsableSourceColumns does not recognize.
    for (const constraint of columnConstraints) {
      const uuid = constraint.structuredTableColumn?.provenanceUuid as ParameterStructuredTableColumnUuid;
      if (
        uuid &&
        !seenUuids.has(uuid) &&
        entityColumnUuids.has(uuid) &&
        constraint.columnDefinition &&
        !columnUuidsUsedByPlateRows.has(uuid)
      ) {
        baseColumns.push({
          name: getColumnLabel(constraint.columnDefinition),
          provenanceUuid: uuid,
          itemType: NotebookColumnItemType.ENTITY,
          // schemaId is intentionally omitted: ROW_DELEGATE entity columns can reference
          // different entity schemas per row (e.g. different material types), so the lookup
          // config modal should show all entity schemas rather than filtering to one.
        });
        seenUuids.add(uuid);
      }
    }

    return baseColumns;
  }

  override allowPasteTable(): boolean {
    return false;
  }

  // Keep in sync with ParameterTableInstantiator.allow_delete_table in
  // benchling/notebook/structured_tables/instantiators/parameter_table_instantiator.py
  // Do not allow deleting any rows from Material Output tables in Execution because they cannot be inserted
  // at Execution.
  override allowDeleteTable(structuredTable: ParameterStructuredTable): {
    canDelete: boolean;
    reason?: string;
  } {
    const superResult = super.allowDeleteTable(structuredTable);
    if (!superResult.canDelete) {
      return superResult;
    }
    if (this.isMaterialOutputExecutionTable(structuredTable)) {
      return {
        canDelete: false,
        reason: 'Cannot delete Material Output Confirmation tables during an experiment',
      };
    }
    return {canDelete: true};
  }

  override getTableIcon(structuredTable: ParameterStructuredTable): BenchlingIconName {
    return ParameterStructuredTableModeToIcon[structuredTable.metadata.mode];
  }

  /** Returns the column index of the Parameter value column. (Which should always be the first non-reference
   * row-delegate column) */
  getParameterValueColumnIndex(structuredTable: ParameterStructuredTable): number {
    return structuredTable.columns.findIndex(
      (column) =>
        column.columnGroup !== StructuredTableColumnGroups.REFERENCE &&
        column.columnType === TableColumnType.ROW_DELEGATE
    );
  }

  getDelegateColumnForParameterDataType<TParameterInfo>(
    column: ColumnInfo<TParameterInfo>,
    parameterInfo: ParameterInfo
  ): StructuredTableColumn {
    return makeStructuredTableColumn({
      columnType:
        parameterInfo.dataType === FieldType.TEXT
          ? TableColumnType.STRING
          : TableColumnType.BIOENTITY_SELECTOR,
      name: column.colName,
      columnGroup: column.colGroup,
      isReadOnly: column.colGroup === StructuredTableColumnGroups.REFERENCE,
    });
  }

  getExistingDelegateColumnForParameterDataType<TParameterInfo>(
    structuredTable: ParameterStructuredTable,
    column: ColumnInfo<TParameterInfo>,
    parameterInfo: ParameterInfo,
    delegatedColumns: Array<StructuredTableColumn>
  ): StructuredTableColumn | null {
    const doesDelegateColumnExist = (col: StructuredTableColumn): boolean =>
      col.columnType ===
        (parameterInfo.dataType === FieldType.TEXT
          ? TableColumnType.STRING
          : TableColumnType.BIOENTITY_SELECTOR) && col.name === column.colName;

    // Check if there is already a delegated column for this parameter's data type and column name
    const existingDelegateColumn = structuredTable.delegatedColumns.find(doesDelegateColumnExist);

    if (existingDelegateColumn) {
      return existingDelegateColumn;
    }

    // Check if there is already a premade delegated column that matches this parameter's data type and column name from the parameters being added
    const premadeDelegateColumn = delegatedColumns.find(doesDelegateColumnExist);

    return premadeDelegateColumn ?? null;
  }

  override isPositionReadOnly(
    currentColumnIndex: number,
    row: EvalRowView<ParameterStructuredTableRowMetadata>,
    structuredTable: ParameterStructuredTable
  ): boolean {
    // prevent editing rows for plate parameters
    if (row.metadata.isPlateParameter || row.metadata.isTombstoned) {
      return true;
    }
    const currentColumn = this.getColumnsForRow(structuredTable, row)[currentColumnIndex];
    // Any non schematized columns that aren't reference columns should be editable in Parameter tables
    // (like Recorded At and Comments)
    if (
      currentColumn.columnType !== TableColumnType.TAG_VALUE &&
      currentColumn.columnGroup !== StructuredTableColumnGroups.REFERENCE
    ) {
      return false;
    }
    return super.isPositionReadOnly(currentColumnIndex, row, structuredTable);
  }

  override fromTable(updatedTable: TableData): ParameterStructuredTable {
    let structuredTable = super.fromTable(updatedTable);
    structuredTable = this.clearColumnDefaultValues(structuredTable);
    return structuredTable;
  }

  addParametersToTable<TParameterInfo extends ParameterInfo>(
    structuredTable: ParameterStructuredTable,
    parameters: Array<TParameterInfo>,
    addRowsFn: StructuredTableAddOperationParameters<
      ParameterStructuredTableMetadata,
      ParameterStructuredTableRowMetadata
    >['addFullRows']
  ): void {
    // Track rows and delegated columns to add to the table
    const newRows: Array<StructuredTableRow<ParameterStructuredTableRowMetadata>> = [];
    const delegatedColumns: Array<StructuredTableColumn> = [];
    // Find the current row index (needed to make new blank rows)
    let rowIdx = structuredTable.rows.length;

    const primaryValueColumnIndex = this.getParameterValueColumnIndex(structuredTable);
    // Iterate through parameters to add to the table
    for (const parameterInfo of parameters) {
      const isPlateParameter =
        parameterInfo.confirmationMethod === ProcedureParameterConfirmationMethod.PLATE_MAP;
      const rowMetadata: ParameterStructuredTableRowMetadata = {
        delegatedColumns: {},
        parameterId: parameterInfo.paramId,
        parameterVersionId: parameterInfo.versionId,
        isPlateParameter,
        plannedValueId: parameterInfo.plannedValueId || undefined,
        methodExecutionInstanceId: parameterInfo.conditionReplicateInfo?.procedureMethodExecutionInstanceId,
        parameterDataType: parameterInfo.dataType,
      };
      const newRow = this.getBlankRow(structuredTable.metadata, rowIdx, structuredTable.columns, rowMetadata);
      // Iterate through the expected columns based on the table type.
      // We know that the table mode determines the parameterTableColumns, and that their type will match that of the given
      // parameters, but typescript doesn't know that, so we have to cast to say that it matches.
      const parameterTableColumnInfos = getParameterStructuredTableColumnInfos(
        structuredTable.metadata.mode,
        {isMultibatch: structuredTable.metadata.isMultibatch}
      );
      for (const columnInfo of parameterTableColumnInfos) {
        // get the column index of the cell by column name
        const cellIndex = structuredTable.columns.findIndex((col) => col.name === columnInfo.colName);
        if (cellIndex === -1) {
          continue;
        }
        // set the cell value
        if (
          // Only fill in the cell value for reference columns
          columnInfo.getFromPInfo &&
          columnInfo.colGroup === StructuredTableColumnGroups.REFERENCE
        ) {
          // Sets the the cell value based on whether the value is a string or a TypedValue
          const value = columnInfo.getFromPInfo(parameterInfo, structuredTable.metadata.mode);
          if (typeof value === 'string' || value == null) {
            // string values should just be added directly to a cell's text
            newRow.cells[cellIndex].text = value ?? '';
          } else {
            newRow.cells[cellIndex].typedValue = value;
          }
        }
        // Set up column delegates if needed
        if (columnInfo.colType === TableColumnType.ROW_DELEGATE) {
          if (columnInfo.shouldDelegateColumnByDataType) {
            const existingDelegateColumn = this.getExistingDelegateColumnForParameterDataType(
              structuredTable,
              columnInfo,
              parameterInfo,
              delegatedColumns
            );
            const delegateColumn =
              existingDelegateColumn ?? this.getDelegateColumnForParameterDataType(columnInfo, parameterInfo);
            newRow.metadata.delegatedColumns![structuredTable.columns[cellIndex].provenanceUuid] =
              delegateColumn.provenanceUuid;
            if (existingDelegateColumn == null) {
              // Only add the delegate column if it doesn't already exist
              delegatedColumns.push(delegateColumn);
            }
          } else if (columnInfo.schemaFieldName != null) {
            // PLATE PARAMETERS
            if (isPlateParameter) {
              // in the value column, add a message to confirm in a plate map
              newRow.cells[cellIndex] = {
                text: cellIndex === primaryValueColumnIndex ? CONFIRM_IN_PLATE_MAP_TEXT : '',
              };
              // add a plain string delegated column instead of a schema field (if one doesn't exist already)
              let plateDelegatedColumn = [...structuredTable.delegatedColumns, ...delegatedColumns].find(
                (delegatedColumn) => delegatedColumn.name === PLATE_STRING_DELEGATED_COLUMN.name
              );
              if (!plateDelegatedColumn) {
                plateDelegatedColumn = PLATE_STRING_DELEGATED_COLUMN;
                delegatedColumns.push(plateDelegatedColumn);
              }
              newRow.metadata.delegatedColumns![structuredTable.columns[cellIndex].provenanceUuid] =
                plateDelegatedColumn.provenanceUuid;

              // TABLE PARAMETERS
            } else {
              // schema field should exist
              const schemaField = parameterInfo.schemaFields[columnInfo.schemaFieldName];
              if (
                assertAndContinue(
                  schemaField != null,
                  `parameter '${parameterInfo.name}' does not have a schema field named '${columnInfo.schemaFieldName}'`
                )
              ) {
                // check for existing delegate column on the table
                const existingColumn = this.getDelegateForSchemaField(structuredTable, schemaField.apiId);
                // check for existing delegate column from this or other parameters that are currently being added
                const premadeColumn = delegatedColumns.find(
                  (col) => col.schemaFieldApiId === schemaField.apiId
                );
                // if there is no existing column, make a new one
                const delegatedColumn =
                  existingColumn ?? premadeColumn ?? makeSchemaFieldStructuredColumn(schemaField, {});
                if (existingColumn == null && premadeColumn == null) {
                  delegatedColumns.push(delegatedColumn);
                }
                newRow.metadata.delegatedColumns![structuredTable.columns[cellIndex].provenanceUuid] =
                  delegatedColumn.provenanceUuid;
              } else {
                // We have no schema field information, so no delegate column
                newRow.metadata.delegatedColumns![structuredTable.columns[cellIndex].provenanceUuid] = '';
              }
            }
          }
        }
      }

      for (const column of structuredTable.columns) {
        // if there are any delegated columns that do not have an entry in the delegated columns map, add an
        // empty delegated column for them
        if (
          column.columnType === TableColumnType.ROW_DELEGATE &&
          !newRow.metadata.delegatedColumns![column.provenanceUuid]
        ) {
          let emptyDelegatedColumn = [...structuredTable.delegatedColumns, ...delegatedColumns].find(
            (delegatedColumn) => delegatedColumn.name === EMPTY_DELEGATED_COLUMN.name
          );
          if (!emptyDelegatedColumn) {
            emptyDelegatedColumn = EMPTY_DELEGATED_COLUMN;
            delegatedColumns.push(emptyDelegatedColumn);
          }
          newRow.metadata.delegatedColumns![column.provenanceUuid] = emptyDelegatedColumn.provenanceUuid;
        }
      }

      newRows.push(newRow);
      rowIdx += 1;
    }

    // Get the new metadata and schemas for the table from new rows and delegated columns, removing duplicates
    const metadata = produce(structuredTable.metadata, (draft) => {
      const allSchemaMetadatas = draft.schemaMetadata.concat(
        getTableSchemaMetadatasFromParameterInfos(parameters)
      );
      draft.schemaMetadata = uniq(allSchemaMetadatas, false, (schemaMetadata) => schemaMetadata.schemaId);
    });
    const schemas = uniq(
      structuredTable.schemas.concat(getTableSchemasFromParameterInfos(parameters)),
      false,
      (schema) => schema.id
    );
    addRowsFn(
      newRows.map((fullRow) => fullRow.cells),
      newRows.map((fullRow) => fullRow.metadata),
      delegatedColumns,
      {metadata, schemas}
    );
  }

  /** Parameter Tables columns should never have default values so this is a helper to clear them out. */
  private clearColumnDefaultValues<
    T extends ParameterStructuredTable | Omit<ParameterStructuredTable, 'cid'>,
  >(structuredTable: T): T {
    return produce(structuredTable, (draft) => {
      for (const column of draft.columns) {
        column.defaultValue = TableUtil.makeEmptyCell();
      }
    });
  }
}
